import axios from 'axios';
import { auth } from './firebase';
import { getEnvVar } from '../utils/env';

interface ChatMessage {
  role: string;
  content: string;
  timestamp?: string;
  id?: string;
  [key: string]: unknown;
}

interface ChatOptions {
  temperature?: number;
  [key: string]: unknown;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
}

interface AxiosErrorWithResponse {
  response?: {
    data?: {
      message?: string;
    };
  };
}

class ChatService {
  private endPointURL: string;

  constructor() {
    this.endPointURL = '/api/chat';

    console.log('[ChatService] Configuration:', {
      endPointURL: this.endPointURL,
      emulatorSetting: getEnvVar('VITE_USE_FIREBASE_EMULATOR'),
      remoteHost: getEnvVar('VITE_REMOTE_API_HOST'),
    });
  }

  async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const idToken = await user.getIdToken();
      return {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      };
    } catch (error) {
      console.error('[ChatService] Failed to get ID token:', error);
      throw error;
    }
  }

  async sendMessage(messages: ChatMessage[], options: ChatOptions = {}): Promise<unknown> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post<ApiResponse>(
        `${this.endPointURL}/message`,
        { messages, options },
        { headers },
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Chat request failed');
      }
    } catch (error) {
      const axiosErr = error as AxiosErrorWithResponse;
      if (axiosErr.response?.data?.message) {
        throw new Error(axiosErr.response.data.message);
      }
      throw error;
    }
  }

  async getCapabilities(): Promise<unknown> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get<ApiResponse>(`${this.endPointURL}/capabilities`, { headers });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get capabilities');
      }
    } catch (error) {
      const axiosErr = error as AxiosErrorWithResponse;
      if (axiosErr.response?.data?.message) {
        throw new Error(axiosErr.response.data.message);
      }
      throw error;
    }
  }

  async checkHealth(): Promise<unknown> {
    try {
      const response = await axios.get(`${this.endPointURL}/health`);
      return response.data;
    } catch (error) {
      console.error('[ChatService] Health check failed:', error);
      throw new Error('Health check failed');
    }
  }

  formatConversation(messages: ChatMessage[]): ChatMessage[] {
    return messages.map((msg) => ({
      ...msg,
      timestamp: msg.timestamp || new Date().toISOString(),
      id: msg.id || Math.random().toString(36).substr(2, 9),
    }));
  }

  validateMessage(message: unknown): boolean {
    if (!message || typeof message !== 'object') return false;
    const msg = message as Record<string, unknown>;
    return (
      typeof msg.role === 'string' &&
      typeof msg.content === 'string' &&
      ['user', 'assistant', 'system'].includes(msg.role)
    );
  }
}

export default new ChatService();
